import { enqueueSnackbar } from '~/utils/notifications';
import type {
    CharacterSubstitution,
    SubstitutionLog,
} from '~/domain/dicom/engine/charset';

/** How many altered values to name before summarising the rest. */
const MAX_LISTED_SUBSTITUTIONS = 3;

function describe(entry: CharacterSubstitution): string {
    return `"${entry.original}" → "${entry.written}"`;
}

/**
 * Tells the user which values the export character set could not represent.
 *
 * Reducing text to fit a character set is not a formatting detail: the
 * receiving system will hold a different structure or patient name than the
 * one on screen here, and the person accountable for the transfer has to know
 * that before they act on it. The toast names the values rather than only
 * their count, so the difference can be checked rather than merely noted.
 *
 * The altered text is user data and may carry identity, so it stays in the
 * toast and out of `logMessage`, which is what the console mirror records.
 */
export function emitSubstitutionToast(log: SubstitutionLog): void {
    if (log.isEmpty) {
        return;
    }
    const listed = log.substitutions
        .slice(0, MAX_LISTED_SUBSTITUTIONS)
        .map(describe)
        .join(', ');
    const remaining = log.substitutions.length - MAX_LISTED_SUBSTITUTIONS;
    const suffix = remaining > 0 ? ` and ${remaining} more` : '';
    const charset = log.substitutions[0].charset;
    const opening = `Exported as ${charset}, which cannot represent every character.`;

    enqueueSnackbar(
        `${opening} The exported file contains ${listed}${suffix}.`,
        {
            variant: 'warning',
            persist: true,
            logMessage: `${opening} ${log.substitutions.length} value(s) were altered.`,
        }
    );
}
